import {
  ArrowLeftIcon,
  BellIcon,
  CalendarBlankIcon,
  CalendarCheckIcon,
  CreditCardIcon,
  PlusIcon,
  ReceiptIcon,
  ScissorsIcon,
  TargetIcon,
  TrashIcon,
  UsersThreeIcon,
  WalletIcon,
} from '@phosphor-icons/react'
import { type PlainDate, isPlainDate } from '@paybae/shared'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { EmptyState } from '@/components/Card'
import { ErrorState, Skeleton } from '@/components/Estado'
import {
  AvisoError,
  Campo,
  FormSheet,
  Guardar,
  Interruptor,
  type OpcionSegmento,
  Segmentos,
  Texto,
} from '@/components/Form'
import { ScreenHeader } from '@/components/ScreenHeader'
import {
  ApiError,
  type NotificationType,
  type Recordatorio,
  type RecordatorioInput,
  type ReglaAviso,
} from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import {
  useActualizarRecordatorio,
  useActualizarReglaAviso,
  useBorrarRecordatorio,
  useCrearRecordatorio,
  useRecordatorios,
  useReglasAviso,
} from '@/lib/queries'

/**
 * Cómo te avisamos.
 *
 * «Más notificaciones configurables» era una petición desde el principio, y la
 * API la tenía resuelta hace tiempo —reglas por tipo y recordatorios propios—
 * sin una sola pantalla que las tocara. Una opción sin puerta es una opción que
 * no existe.
 *
 * DOS CAPAS, NO UNA LISTA
 *
 * Arriba, los avisos que la app genera sola a partir del dinero: el corte, el
 * pago, la nómina, los fijos. De esos no se elige el **qué** —los calcula el
 * motor— sino el **si** y el **cuándo**. Abajo, los recordatorios que uno se
 * pone a mano, donde se elige todo. Mezclarlos en una sola lista haría creer
 * que un aviso de tarjeta se puede borrar como se borra un recordatorio.
 *
 * SE GUARDA AL TOCAR
 *
 * No hay botón de guardar en las reglas. Un interruptor con confirmación
 * diferida es un interruptor que se queda a medias en cuanto alguien sale de la
 * pantalla; y cada cambio es una sola llamada, reversible con el mismo toque.
 */
export function AvisosAjustes() {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const reglas = useReglasAviso()
  const recordatorios = useRecordatorios()

  const [nuevoAbierto, setNuevoAbierto] = useState(false)

  /**
   * El nodo de la sección de recordatorios, en estado y no en `useRef`.
   *
   * Se llega aquí desde dos sitios distintos de Avisos, y el enlace de
   * recordatorios trae `?ver=recordatorios` para no dejar a quien lo tocó
   * mirando la lista de reglas preguntándose dónde quedó lo que buscaba. Con
   * una `ref` no funcionaba: mientras las reglas cargan, la pantalla devuelve
   * el esqueleto y la sección todavía no existe, así que el efecto encontraba
   * `null` y no volvía a intentarlo. En estado, montar el nodo provoca el
   * render que dispara el desplazamiento —justo cuando hay a dónde ir.
   */
  const [nodoRecordatorios, setNodoRecordatorios] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (params.get('ver') !== 'recordatorios' || !nodoRecordatorios) return
    // Salto instantáneo, no suave: la animación arranca antes de que terminen
    // de pintarse los ocho bloques de reglas, y cualquier cambio de altura a
    // media animación la deja a mitad de camino. Además, quien tocó «Tus
    // recordatorios» ya sabe a dónde va; no necesita ver el viaje.
    nodoRecordatorios.scrollIntoView({ behavior: 'auto', block: 'start' })
  }, [params, nodoRecordatorios])

  if (reglas.isPending) return <Cargando />

  if (reglas.isError || !reglas.data) {
    return (
      <>
        <Encabezado onVolver={() => navigate('/avisos')} />
        <ErrorState
          titulo="No pudimos cargar tus ajustes"
          mensaje={reglas.error instanceof Error ? reglas.error.message : 'Inténtalo de nuevo.'}
          onReintentar={() => void reglas.refetch()}
        />
      </>
    )
  }

  return (
    <div data-pantalla="avisos-ajustes">
      <style>{CSS_AJUSTES}</style>

      <Encabezado onVolver={() => navigate('/avisos')} />

      <div className="px-safe mx-auto max-w-lg pb-6">
        <section className="aj-seccion">
          <Rotulo numero="01" titulo="Qué te avisamos" />
          <p className="mb-4 text-[13px] leading-snug text-ink-soft">
            Estos los calcula la app con tus tarjetas, tus fijos y tus ingresos. Aquí eliges cuáles
            quieres y con cuánto tiempo.
          </p>

          <ul className="flex flex-col gap-3">
            {ORDEN_TIPOS.map((tipo) => {
              const regla = reglas.data.find((r) => r.type === tipo)
              if (!regla) return null
              return <BloqueRegla key={tipo} regla={regla} />
            })}
          </ul>
        </section>

        <section className="aj-seccion" ref={setNodoRecordatorios}>
          <Rotulo numero="02" titulo="Tus recordatorios" />
          <p className="mb-4 text-[13px] leading-snug text-ink-soft">
            Los que tú pones: «pagar el predial», «revisar la suscripción del gimnasio». Llegan a
            todo el hogar.
          </p>

          <ListaRecordatorios
            recordatorios={recordatorios.data}
            cargando={recordatorios.isPending}
          />

          <button
            type="button"
            onClick={() => {
              haptic('light')
              setNuevoAbierto(true)
            }}
            className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-card border border-dashed border-line text-[14px] font-semibold text-primary-text active:scale-[0.99]"
          >
            <PlusIcon size={16} weight="bold" />
            Nuevo recordatorio
          </button>
        </section>
      </div>

      {nuevoAbierto ? <FormularioRecordatorio onCerrar={() => setNuevoAbierto(false)} /> : null}
    </div>
  )
}

/* ── Reglas de fábrica ───────────────────────────────────────────────────── */

interface MetaTipo {
  Icono: typeof BellIcon
  label: string
  ayuda: string
  /** Si tiene sentido avisar con días de antelación. */
  antelacion: boolean
  /** Si la hora es elegible. La actividad de la pareja llega al instante. */
  hora: boolean
}

const META: Record<NotificationType, MetaTipo> = {
  CARD_DUE: {
    Icono: CreditCardIcon,
    label: 'Pago de tarjeta',
    ayuda: 'Antes de la fecha límite. Es el aviso que te ahorra los intereses.',
    antelacion: true,
    hora: true,
  },
  CARD_CUTOFF: {
    Icono: ScissorsIcon,
    label: 'Corte de tarjeta',
    ayuda: 'El día que cierra el estado de cuenta: lo que compres después se paga hasta el siguiente.',
    antelacion: true,
    hora: true,
  },
  RECURRING_DUE: {
    Icono: ReceiptIcon,
    label: 'Gastos fijos',
    ayuda: 'Renta, luz, colegiaturas: antes de que venza cada uno.',
    antelacion: true,
    hora: true,
  },
  PAYDAY_SAVINGS_PROMPT: {
    Icono: WalletIcon,
    label: 'Ahorro al cobrar',
    ayuda: 'El día que entra tu sueldo, para apartar el ahorro antes de que se vaya en otra cosa.',
    antelacion: false,
    hora: true,
  },
  GOAL_MILESTONE: {
    Icono: TargetIcon,
    label: 'Metas de ahorro',
    ayuda: 'Cuando una meta llega a la mitad, a tres cuartos, o se cumple.',
    antelacion: false,
    hora: true,
  },
  WEEKLY_SUMMARY: {
    Icono: CalendarCheckIcon,
    label: 'Resumen de la semana',
    ayuda: 'Una vez por semana, en qué se les fue el dinero.',
    antelacion: false,
    hora: true,
  },
  PARTNER_ACTIVITY: {
    Icono: UsersThreeIcon,
    label: 'Lo que registra tu pareja',
    ayuda: 'Cada gasto, al momento. Viene apagado: en un hogar activo se vuelve ruido en días.',
    antelacion: false,
    hora: false,
  },
  CUSTOM: {
    Icono: CalendarBlankIcon,
    // No se llama «Tus recordatorios» como la sección de abajo: dos rótulos
    // idénticos en la misma pantalla harían pensar que llevan al mismo sitio.
    label: 'Recordatorios que tú creas',
    ayuda: 'El interruptor general de los de abajo. Apagarlo los silencia todos de golpe.',
    antelacion: false,
    hora: false,
  },
}

/** De lo que más cuesta olvidar a lo que menos. El orden es la recomendación. */
const ORDEN_TIPOS: NotificationType[] = [
  'CARD_DUE',
  'CARD_CUTOFF',
  'RECURRING_DUE',
  'PAYDAY_SAVINGS_PROMPT',
  'GOAL_MILESTONE',
  'WEEKLY_SUMMARY',
  'PARTNER_ACTIVITY',
  'CUSTOM',
]

/** Antelaciones ofrecidas. El servidor acepta como mucho seis por aviso. */
const DIAS_OFRECIDOS = [0, 1, 2, 3, 5, 7] as const

function etiquetaDias(dias: number): string {
  if (dias === 0) return 'El día'
  if (dias === 1) return '1 día'
  return `${dias} días`
}

/**
 * Un tipo de aviso con su interruptor y, si está encendido, sus detalles.
 *
 * Los detalles solo aparecen con el aviso encendido: elegir la antelación de
 * algo que no va a llegar es trabajo tirado, y ocho bloques desplegados de
 * golpe convierten la pantalla en un panel de control.
 */
function BloqueRegla({ regla }: { regla: ReglaAviso }) {
  const actualizar = useActualizarReglaAviso()
  const meta = META[regla.type]

  /**
   * Copia local mientras el servidor confirma.
   *
   * Sin ella, cada toque deja el interruptor en su posición vieja hasta que
   * vuelve la invalidación —medio segundo en el que parece que no funcionó y se
   * vuelve a tocar, deshaciendo el cambio.
   */
  const [local, setLocal] = useState<ReglaAviso | null>(null)
  const vigente = local ?? regla

  const guardar = (cambio: Partial<ReglaAviso>): void => {
    const siguiente = { ...vigente, ...cambio }
    setLocal(siguiente)
    actualizar.mutate(
      {
        type: regla.type,
        enabled: siguiente.enabled,
        channels: siguiente.channels,
        leadDays: siguiente.leadDays,
        timeOfDay: siguiente.timeOfDay,
      },
      {
        // Si el servidor lo rechaza, se suelta la copia local y vuelve a mandar
        // lo que él dice: dejar la pantalla mostrando un ajuste que no se
        // guardó es peor que no haberlo intentado.
        onError: () => setLocal(null),
      },
    )
  }

  const alternarDia = (dia: number): void => {
    const tiene = vigente.leadDays.includes(dia)
    const siguiente = tiene
      ? vigente.leadDays.filter((d) => d !== dia)
      : [...vigente.leadDays, dia].sort((a, b) => b - a)
    guardar({ leadDays: siguiente })
  }

  return (
    <li className="flex flex-col gap-2">
      <Interruptor
        label={meta.label}
        ayuda={meta.ayuda}
        value={vigente.enabled}
        onChange={(v) => guardar({ enabled: v })}
      />

      {vigente.enabled ? (
        <div className="ml-3 flex flex-col gap-3 border-l border-line pl-3.5">
          {/* El correo, por aviso y no de golpe.
              La API lo soportaba desde el principio y esta pantalla no lo
              enseñaba: para quitar el correo de un solo tipo de aviso había que
              llamar a la API a mano. Va aquí dentro, bajo el interruptor
              principal, porque solo tiene sentido si el aviso está encendido. */}
          <Interruptor
            label="También por correo"
            ayuda={
              vigente.channels.includes('EMAIL')
                ? 'Llega al teléfono y al correo.'
                : 'Solo llega al teléfono.'
            }
            value={vigente.channels.includes('EMAIL')}
            onChange={(v) =>
              guardar({
                // PUSH siempre queda: un aviso sin ningún canal está encendido
                // y no llega a ninguna parte, que es la peor de las dos cosas.
                channels: v ? ['PUSH', 'EMAIL'] : ['PUSH'],
              })
            }
          />

          {meta.antelacion ? (
            <div className="flex flex-col gap-1.5">
              <p className="aj-versalita text-ink-faint">Con cuánta antelación</p>
              <div className="flex flex-wrap gap-1.5">
                {DIAS_OFRECIDOS.map((dia) => {
                  const activo = vigente.leadDays.includes(dia)
                  return (
                    <button
                      key={dia}
                      type="button"
                      aria-pressed={activo}
                      onClick={() => {
                        haptic('selection')
                        alternarDia(dia)
                      }}
                      className={cn(
                        'flex min-h-[44px] items-center rounded-pill px-3.5 text-[13px] font-semibold transition-colors',
                        activo
                          ? 'bg-accion text-on-accion'
                          : 'border border-line bg-surface text-ink-soft',
                      )}
                    >
                      {etiquetaDias(dia)}
                    </button>
                  )
                })}
              </div>
              {vigente.leadDays.length === 0 ? (
                <p className="text-[12px] leading-snug text-expense-text">
                  Sin ningún día marcado no te avisaremos de esto, aunque el interruptor esté
                  encendido.
                </p>
              ) : null}
            </div>
          ) : null}

          {meta.hora ? (
            <div className="flex flex-col gap-1.5">
              <p className="aj-versalita text-ink-faint">A qué hora</p>
              <SelectorHora
                valor={vigente.timeOfDay}
                onCambio={(hora) => guardar({ timeOfDay: hora })}
              />
            </div>
          ) : null}

          {vigente.source === 'HOUSEHOLD' ? (
            <p className="text-[12px] leading-snug text-ink-faint">
              Este ajuste lo puso quien administra el hogar. Al cambiarlo, se vuelve tuyo y deja de
              seguir al del hogar.
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

/**
 * Hora del día.
 *
 * Campo nativo `time`: abre el selector del sistema, respeta el formato de 12 o
 * 24 horas del teléfono y ya sabe que las 25:00 no existen. Un desplegable
 * propio de 24 opciones sería más código para hacerlo peor.
 */
function SelectorHora({ valor, onCambio }: { valor: string; onCambio: (v: string) => void }) {
  return (
    <input
      type="time"
      value={valor}
      onChange={(e) => {
        const v = e.target.value
        // El campo emite cadena vacía mientras se edita a medias; mandarla
        // borraría la hora en el servidor.
        if (/^([01]\d|2[0-3]):[0-5]\d$/.test(v)) onCambio(v)
      }}
      className="min-h-[48px] w-fit rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
    />
  )
}

/* ── Recordatorios ───────────────────────────────────────────────────────── */

function ListaRecordatorios({
  recordatorios,
  cargando,
}: {
  recordatorios: Recordatorio[] | undefined
  cargando: boolean
}) {
  const actualizar = useActualizarRecordatorio()
  const borrar = useBorrarRecordatorio()

  if (cargando) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-[68px]" />
        <Skeleton className="h-[68px]" />
      </div>
    )
  }

  if (!recordatorios || recordatorios.length === 0) {
    return (
      <div className="rounded-card border border-line bg-surface">
        <EmptyState
          icon={<CalendarBlankIcon size={26} />}
          title="Todavía no tienes recordatorios"
          description="Crea el primero para lo que la app no puede adivinar: el predial, la verificación del coche, la renovación del seguro."
        />
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-2">
      {recordatorios.map((r) => (
        <li
          key={r.id}
          className="flex items-center gap-2.5 rounded-card border border-line bg-surface px-3.5 py-2.5"
        >
          <div className="min-w-0 flex-1">
            <p className={cn('truncate text-[14px] font-semibold', r.enabled ? 'text-ink' : 'text-ink-faint')}>
              {r.title}
            </p>
            {/* La cadencia no se trunca: «Una vez, el 18 de septiembre a las 0…»
                corta justo en la hora, que es la mitad del dato. Envuelve en dos
                líneas y la fila crece, que es más barato que perder la hora. */}
            <p className="text-[12px] leading-snug text-ink-soft">{describirCuando(r)}</p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={r.enabled}
            aria-label={`${r.enabled ? 'Apagar' : 'Encender'} ${r.title}`}
            onClick={() => {
              haptic('selection')
              actualizar.mutate({ id: r.id, enabled: !r.enabled })
            }}
            className="flex size-11 shrink-0 items-center justify-center"
          >
            <span
              className={cn(
                'relative h-6 w-10 rounded-pill transition-colors duration-200',
                r.enabled ? 'bg-primary' : 'bg-surface-sunken',
              )}
            >
              {/* `left-0` explícito, no la posición estática: un `<button>`
                  hereda `text-align: center` del navegador, y sin anclar el
                  origen la perilla nacía centrada y el desplazamiento la sacaba
                  media pastilla fuera del riel. */}
              <span
                className={cn(
                  'absolute left-0 top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform duration-200',
                  r.enabled ? 'translate-x-[1.125rem]' : 'translate-x-0.5',
                )}
              />
            </span>
          </button>

          <button
            type="button"
            aria-label={`Borrar ${r.title}`}
            onClick={() => {
              haptic('warning')
              borrar.mutate(r.id)
            }}
            className="flex size-11 shrink-0 items-center justify-center rounded-full text-ink-faint active:text-expense-text"
          >
            <TrashIcon size={17} />
          </button>
        </li>
      ))}
    </ul>
  )
}

/** Cada cuándo se repite, en la voz de quien lo puso. */
function describirCuando(r: Recordatorio): string {
  const hora = r.timeOfDay

  if (r.scheduleType === 'ONCE') {
    return r.runAt ? `Una vez, el ${fechaLarga(r.runAt)} a las ${hora}` : `Una vez, a las ${hora}`
  }

  if (r.scheduleType === 'RELATIVE_TO_ENTITY') {
    const dias = r.offsetDays ?? 0
    if (dias === 0) return `El mismo día del evento, a las ${hora}`
    return dias < 0
      ? `${Math.abs(dias)} días antes del evento, a las ${hora}`
      : `${dias} días después del evento, a las ${hora}`
  }

  if (r.frequency === 'WEEKLY' || r.frequency === 'BIWEEKLY') {
    const dia = DIAS_SEMANA_LARGOS[r.dayOfWeek ?? 1] ?? 'lunes'
    return r.frequency === 'WEEKLY'
      ? `Cada ${dia} a las ${hora}`
      : `Cada dos semanas, ${dia} a las ${hora}`
  }

  if (r.dayOfMonth) return `El día ${r.dayOfMonth} de cada mes, a las ${hora}`
  return `Se repite, a las ${hora}`
}

type Cadencia = 'ONCE' | 'MENSUAL' | 'SEMANAL'

const OPCIONES_CADENCIA: OpcionSegmento<Cadencia>[] = [
  { value: 'ONCE', label: 'Una vez', detalle: 'En una fecha' },
  { value: 'MENSUAL', label: 'Cada mes', detalle: 'Un día fijo' },
  { value: 'SEMANAL', label: 'Cada semana', detalle: 'Un día fijo' },
]

const DIAS_SEMANA = ['D', 'L', 'M', 'M', 'J', 'V', 'S']
const DIAS_SEMANA_LARGOS = [
  'domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado',
]

/**
 * Alta de un recordatorio.
 *
 * Cubre las dos formas que se piden solas —una fecha suelta y algo que se
 * repite— y deja fuera el anclaje a una tarjeta o a un fijo concreto: para eso
 * ya existen los avisos automáticos de arriba, que salen del propio calendario
 * del hogar y no hay que mantener a mano.
 */
function FormularioRecordatorio({ onCerrar }: { onCerrar: () => void }) {
  const crear = useCrearRecordatorio()

  const [titulo, setTitulo] = useState('')
  const [nota, setNota] = useState('')
  const [cadencia, setCadencia] = useState<Cadencia>('ONCE')
  const [fecha, setFecha] = useState('')
  const [diaMes, setDiaMes] = useState('1')
  const [diaSemana, setDiaSemana] = useState(1)
  const [hora, setHora] = useState('09:00')
  const [porCorreo, setPorCorreo] = useState(false)

  const [errores, setErrores] = useState<Record<string, string[]>>({})
  const [error, setError] = useState<string | null>(null)

  const guardar = (): void => {
    setError(null)
    setErrores({})

    if (titulo.trim().length < 2) {
      setErrores({ title: ['Ponle un título al recordatorio'] })
      return
    }
    if (cadencia === 'ONCE' && !isPlainDate(fecha)) {
      setErrores({ runAt: ['Elige el día del recordatorio'] })
      return
    }

    const dia = Number(diaMes)
    if (cadencia === 'MENSUAL' && (!Number.isInteger(dia) || dia < 1 || dia > 31)) {
      setErrores({ dayOfMonth: ['Un día del 1 al 31'] })
      return
    }

    const base: RecordatorioInput = {
      title: titulo.trim(),
      body: nota.trim() || null,
      channels: porCorreo ? ['PUSH', 'EMAIL'] : ['PUSH'],
      timeOfDay: hora,
      scheduleType: cadencia === 'ONCE' ? 'ONCE' : 'RECURRING',
    }

    const carga: RecordatorioInput =
      cadencia === 'ONCE'
        ? { ...base, runAt: fecha as PlainDate }
        : cadencia === 'MENSUAL'
          ? { ...base, frequency: 'MONTHLY', dayOfMonth: dia }
          : { ...base, frequency: 'WEEKLY', dayOfWeek: diaSemana }

    crear.mutate(carga, {
      onSuccess: () => {
        haptic('success')
        onCerrar()
      },
      onError: (e) => {
        if (e instanceof ApiError && e.fieldErrors) {
          setErrores(e.fieldErrors)
          setError(null)
          return
        }
        setError(e instanceof Error ? e.message : 'No se pudo guardar. Inténtalo de nuevo.')
      },
    })
  }

  return (
    <FormSheet
      tinte="butter"
      titulo="Nuevo recordatorio"
      onCerrar={onCerrar}
      accion={
        <Guardar onClick={guardar} guardando={crear.isPending}>
          Crear recordatorio
        </Guardar>
      }
    >
      <AvisoError mensaje={error} />

      <Campo label="¿De qué te recordamos?" error={errores.title?.[0]}>
        <Texto
          value={titulo}
          onChange={setTitulo}
          placeholder="Pagar el predial"
          maxLength={120}
          invalido={Boolean(errores.title)}
        />
      </Campo>

      <Campo label="Nota" ayuda="Opcional. Lo que quieras recordar además del título.">
        <Texto value={nota} onChange={setNota} placeholder="En la app del municipio" maxLength={500} />
      </Campo>

      <Campo label="¿Cada cuándo?">
        <Segmentos opciones={OPCIONES_CADENCIA} value={cadencia} onChange={setCadencia} columnas={3} />
      </Campo>

      {cadencia === 'ONCE' ? (
        <Campo label="¿Qué día?" error={errores.runAt?.[0]}>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
          />
        </Campo>
      ) : null}

      {cadencia === 'MENSUAL' ? (
        <Campo
          label="¿Qué día del mes?"
          ayuda="Del 1 al 31. En los meses cortos se corre al último día."
          error={errores.dayOfMonth?.[0]}
        >
          <Texto
            value={diaMes}
            onChange={setDiaMes}
            inputMode="numeric"
            maxLength={2}
            invalido={Boolean(errores.dayOfMonth)}
          />
        </Campo>
      ) : null}

      {cadencia === 'SEMANAL' ? (
        <Campo label="¿Qué día de la semana?" error={errores.dayOfWeek?.[0]}>
          <div className="flex gap-1.5">
            {DIAS_SEMANA.map((letra, i) => {
              const activo = i === diaSemana
              return (
                <button
                  key={`${letra}-${i}`}
                  type="button"
                  aria-pressed={activo}
                  aria-label={DIAS_SEMANA_LARGOS[i]}
                  onClick={() => {
                    haptic('selection')
                    setDiaSemana(i)
                  }}
                  className={cn(
                    'flex size-11 flex-1 items-center justify-center rounded-full text-[14px] font-semibold transition-colors',
                    activo ? 'bg-accion text-on-accion' : 'border border-line bg-surface text-ink-soft',
                  )}
                >
                  {letra}
                </button>
              )
            })}
          </div>
        </Campo>
      ) : null}

      <Campo label="¿A qué hora?" error={errores.timeOfDay?.[0]}>
        <SelectorHora valor={hora} onCambio={setHora} />
      </Campo>

      <Interruptor
        label="También por correo"
        ayuda="Además de la notificación del teléfono. Útil si sueles tenerlo en silencio."
        value={porCorreo}
        onChange={setPorCorreo}
      />
    </FormSheet>
  )
}

/* ── Marco ───────────────────────────────────────────────────────────────── */

function Encabezado({ onVolver }: { onVolver: () => void }) {
  return (
    <ScreenHeader
      title="Cómo te avisamos"
      subtitle="Elige qué llega, cuándo y a qué hora"
      tinte="butter"
      action={
        <button
          type="button"
          onClick={() => {
            haptic('light')
            onVolver()
          }}
          className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill border border-line bg-surface px-3.5 text-[13px] font-semibold text-ink active:scale-95"
        >
          <ArrowLeftIcon size={15} weight="bold" />
          Avisos
        </button>
      }
    />
  )
}

function Rotulo({ numero, titulo }: { numero: string; titulo: string }) {
  return (
    <header className="mb-2 flex items-baseline gap-2.5">
      <span className="aj-fechado shrink-0">{numero}</span>
      <h2 className="min-w-0 flex-1 truncate font-display text-[19px] font-bold tracking-[-0.035em] text-ink">
        {titulo}
      </h2>
    </header>
  )
}

function Cargando() {
  return (
    <div data-pantalla="avisos-ajustes">
      <style>{CSS_AJUSTES}</style>
      <ScreenHeader title="Cómo te avisamos" subtitle="Cargando tus ajustes…" tinte="butter" />
      <div className="px-safe mx-auto flex max-w-lg flex-col gap-2.5 pt-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[72px]" />
        ))}
      </div>
    </div>
  )
}

const CSS_AJUSTES = `
[data-pantalla='avisos-ajustes'] .aj-seccion {
  border-top: 1px solid var(--line);
  padding-top: 1.1rem;
  margin-top: 1.5rem;
}
[data-pantalla='avisos-ajustes'] .aj-seccion:first-of-type { margin-top: 0.75rem; }

[data-pantalla='avisos-ajustes'] .aj-versalita {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}
[data-pantalla='avisos-ajustes'] .aj-fechado {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
`

const MESES_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/**
 * Se parte la cadena `AAAA-MM-DD` a mano en vez de crear un `Date`: un
 * `new Date('2026-08-09')` se interpreta en UTC y en México se lee un día antes.
 */
function fechaLarga(fecha: PlainDate): string {
  const [, mes, dia] = fecha.split('-')
  return `${Number(dia)} de ${MESES_LARGOS[Number(mes) - 1]}`
}
